import React from 'react';
import RichTextEditor from '../RichTextEditor';

export type CharacteristicRow = { label: string; value: string };

export interface CourseAboutPageFieldsProps {
  longDescription: string;
  onLongDescriptionChange: (html: string) => void;
  learningOutcomes: string[];
  onLearningOutcomesChange: (rows: string[]) => void;
  summaryBullets: string[];
  onSummaryBulletsChange: (rows: string[]) => void;
  characteristics: CharacteristicRow[];
  onCharacteristicsChange: (rows: CharacteristicRow[]) => void;
}

/**
 * محتوای تب «درباره دوره» در سایت — برای مودال ایجاد/ویرایش دوره
 */
const CourseAboutPageFields: React.FC<CourseAboutPageFieldsProps> = ({
  longDescription,
  onLongDescriptionChange,
  learningOutcomes,
  onLearningOutcomesChange,
  summaryBullets,
  onSummaryBulletsChange,
  characteristics,
  onCharacteristicsChange,
}) => {
  const loRows = learningOutcomes.length ? learningOutcomes : [''];
  const sbRows = summaryBullets.length ? summaryBullets : [''];
  const chRows = characteristics.length ? characteristics : [{ label: '', value: '' }];

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-4 space-y-3">
        <p className="text-sm font-bold text-indigo-950 border-b border-indigo-200 pb-2">خصوصیات دوره (کارت‌ها)</p>
        <p className="text-xs text-indigo-800/90">
          برای هر کارت یک عنوان و یک مقدار وارد کنید (مثل «تعداد جلسات» و «۲ جلسه»). می‌توانید چند ردیف اضافه کنید.
        </p>
        {chRows.map((row, idx) => (
          <div key={`ch-${idx}`} className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">عنوان کارت</label>
              <input
                type="text"
                value={row.label}
                onChange={(e) => {
                  const next = [...chRows];
                  next[idx] = { ...next[idx], label: e.target.value };
                  onCharacteristicsChange(next);
                }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                placeholder="مثلاً تعداد جلسات"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">مقدار</label>
              <input
                type="text"
                value={row.value}
                onChange={(e) => {
                  const next = [...chRows];
                  next[idx] = { ...next[idx], value: e.target.value };
                  onCharacteristicsChange(next);
                }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                placeholder="مثلاً ۱۲ جلسه"
              />
            </div>
            <button
              type="button"
              className="text-xs text-red-600 px-2 py-1 shrink-0"
              onClick={() => {
                const next = [...chRows];
                next.splice(idx, 1);
                onCharacteristicsChange(next.length ? next : [{ label: '', value: '' }]);
              }}
            >
              حذف ردیف
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-xs font-semibold text-indigo-600"
          onClick={() => onCharacteristicsChange([...chRows, { label: '', value: '' }])}
        >
          + افزودن کارت خصوصیت
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-sm font-bold text-slate-800 border-b border-slate-200 pb-2">اینجا چه چیزهایی یاد می‌گیرید</p>
        <p className="text-xs text-slate-600">هر آیتم در همان قالب کارت در سایت نمایش داده می‌شود.</p>
        {loRows.map((line, idx) => (
          <div key={`lo-${idx}`} className="flex gap-2">
            <input
              type="text"
              value={line}
              onChange={(e) => {
                const base = [...loRows];
                base[idx] = e.target.value;
                onLearningOutcomesChange(base);
              }}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
              placeholder={`نکته ${idx + 1}`}
            />
            <button
              type="button"
              className="text-xs text-red-600 px-2"
              onClick={() => {
                const base = [...loRows];
                base.splice(idx, 1);
                onLearningOutcomesChange(base.length ? base : []);
              }}
            >
              حذف
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-xs font-semibold text-indigo-600"
          onClick={() => onLearningOutcomesChange([...learningOutcomes, ''])}
        >
          + افزودن نکته یادگیری
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <p className="text-sm font-bold text-slate-800 border-b border-slate-200 pb-2">نکات خلاصه (زیر عنوان دوره)</p>
        {sbRows.map((line, idx) => (
          <div key={`sb-${idx}`} className="flex gap-2">
            <input
              type="text"
              value={line}
              onChange={(e) => {
                const base = [...sbRows];
                base[idx] = e.target.value;
                onSummaryBulletsChange(base);
              }}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
              placeholder={`خلاصه ${idx + 1}`}
            />
            <button
              type="button"
              className="text-xs text-red-600 px-2"
              onClick={() => {
                const base = [...sbRows];
                base.splice(idx, 1);
                onSummaryBulletsChange(base.length ? base : []);
              }}
            >
              حذف
            </button>
          </div>
        ))}
        <button
          type="button"
          className="text-xs font-semibold text-indigo-600"
          onClick={() => onSummaryBulletsChange([...summaryBullets, ''])}
        >
          + افزودن خط خلاصه
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <p className="text-sm font-bold text-slate-800 px-4 pt-4 pb-2 border-b border-slate-100">درباره این دوره (متن کامل)</p>
        <p className="text-xs text-slate-600 px-4 pt-2">
          ویرایشگر متن کامل؛ در سایت در بخش «درباره این دوره» نمایش داده می‌شود.
        </p>
        <div className="p-4">
          <div
            className="rounded-lg border border-slate-200 overflow-hidden shadow-sm"
            style={{ direction: 'rtl' }}
          >
            <RichTextEditor
              value={longDescription}
              onChange={onLongDescriptionChange}
              height={400}
              placeholder="معرفی کامل دوره، مخاطب، سرفصل ذهنی، خروجی..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseAboutPageFields;
